import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Smart link that handles "/path#anchor" hrefs reliably:
 *  - If you're already on the target path, it just scrolls to the anchor.
 *  - Otherwise it navigates, then scrolls once the new page mounts.
 *  - Falls back to a normal anchor for pure hashes ("#section").
 *  - Plain "/path" hrefs route via react-router as expected.
 */
export default function HashLink({ to, children, className, onClick, ...rest }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e) => {
    // Allow modified clicks (cmd/ctrl/middle) to do their default thing
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

    if (!to) return;

    // Pure hash anchor on current page (#foo) — just smooth-scroll
    if (typeof to === 'string' && to.startsWith('#')) {
      e.preventDefault();
      const el = document.getElementById(to.slice(1));
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onClick?.(e);
      return;
    }

    // Path + hash like "/#programs" or "/donate#sponsorship"
    if (typeof to === 'string' && to.includes('#')) {
      e.preventDefault();
      const [path, hash] = to.split('#');
      const samePath = (path || '/') === location.pathname;
      if (samePath) {
        const el = document.getElementById(hash);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Update the URL hash without re-triggering navigation
        window.history.replaceState(null, '', `${location.pathname}#${hash}`);
      } else {
        // Navigate, then scroll on the next tick once the new page has rendered
        navigate(path || '/');
        setTimeout(() => {
          const el = document.getElementById(hash);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.history.replaceState(null, '', `${path || '/'}#${hash}`);
        }, 80);
      }
      onClick?.(e);
      return;
    }

    // Plain path -> standard navigate
    e.preventDefault();
    navigate(to);
    onClick?.(e);
  };

  return (
    <a href={to} className={className} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
